import { z } from 'zod'

/**
 * Tool-contract completeness rules — Part Three C7, register §6.
 *
 * Every tool declares its fields (required/optional, nullability, enums, units and
 * bounds), stable identifiers and their revision semantics, pagination/ordering and
 * cursor invalidation, all four output shapes, its authorization/entitlement/host
 * policy decisions, idempotency scope and post-retention behaviour, side-effect
 * annotations with approval requirements, and its retry/reconciliation policy.
 * There is no generic unrestricted `execute` tool.
 */

export const ToolValueType = z.enum([
  'string',
  'integer',
  'number',
  'boolean',
  'datetime',
  'object',
  'array',
  'enum',
  'binary_ref',
])
export type ToolValueType = z.infer<typeof ToolValueType>

export const ToolFieldSpec = z
  .object({
    name: z.string().min(1).max(128),
    type: ToolValueType,
    /** Nullability is explicit: absence and `null` are different states (C7). */
    nullable: z.boolean(),
    enum_values: z.array(z.string().min(1).max(128)).optional(),
    /** Unit for numeric fields (`ms`, `bytes`, `count`, …). */
    unit: z.string().min(1).max(64).optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    description_key: z.string().min(1).max(160),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (
      field.type === 'enum' &&
      (field.enum_values === undefined || field.enum_values.length === 0)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'an enum field declares its values',
        path: ['enum_values'],
      })
    }
    if (field.type !== 'enum' && field.enum_values !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'enum_values is only valid for enum fields',
        path: ['enum_values'],
      })
    }
    if (
      field.minimum !== undefined &&
      field.maximum !== undefined &&
      field.minimum > field.maximum
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'minimum must not exceed maximum',
        path: ['minimum'],
      })
    }
  })
export type ToolFieldSpec = z.infer<typeof ToolFieldSpec>

export const ToolStableIdentifier = z
  .object({
    name: z.string().min(1).max(128),
    description_key: z.string().min(1).max(160),
    revision_semantics: z.enum(['MONOTONIC', 'CONTENT_DIGEST', 'IMMUTABLE']),
  })
  .strict()
export type ToolStableIdentifier = z.infer<typeof ToolStableIdentifier>

export const ToolCursorInvalidation = z.enum([
  'ON_REVISION_CHANGE',
  'ON_MUTATION',
  'ON_RETENTION_EXPIRY',
])
export type ToolCursorInvalidation = z.infer<typeof ToolCursorInvalidation>

export const ToolPagination = z
  .object({
    supported: z.boolean(),
    ordering: z.array(z.string().min(1).max(64)).min(1),
    /** Bounds are register-owned (`workspace_pagination_*`); validated there. */
    default_limit: z.number().int().positive(),
    max_limit: z.number().int().positive(),
    cursor_invalidation: ToolCursorInvalidation,
  })
  .strict()
  .superRefine((pagination, ctx) => {
    if (pagination.default_limit > pagination.max_limit) {
      ctx.addIssue({
        code: 'custom',
        message: 'default_limit must not exceed max_limit',
        path: ['default_limit'],
      })
    }
  })
export type ToolPagination = z.infer<typeof ToolPagination>

/** All four output shapes are declared; `error` is always the canonical envelope. */
export const ToolOutputs = z
  .object({
    success: z.string().min(1).max(128),
    pending: z.string().min(1).max(128).optional(),
    partial_success: z.string().min(1).max(128).optional(),
    error: z.literal('ErrorEnvelope'),
  })
  .strict()
export type ToolOutputs = z.infer<typeof ToolOutputs>

/** The three independent policy decisions rechecked at execution (C4, C7). */
export const ToolPolicyDecisions = z
  .object({
    authorization: z.array(z.string().min(1).max(128)).min(1),
    entitlement: z.array(z.string().min(1).max(128)).min(1),
    host_policy: z.array(z.string().min(1).max(128)).min(1),
  })
  .strict()
export type ToolPolicyDecisions = z.infer<typeof ToolPolicyDecisions>

export const ToolIdempotency = z
  .object({
    scope: z.enum(['ACCOUNT_OPERATION_CLIENT_REQUEST_ID', 'LOGICAL_MESSAGE_ID', 'NONE']),
    key_fields: z.array(z.string().min(1).max(128)),
    post_retention_behaviour: z.enum(['REJECT_AND_RECONCILE', 'RECONCILE_VIA_BUSINESS_UNIQUENESS']),
  })
  .strict()
  .superRefine((idempotency, ctx) => {
    if (idempotency.scope !== 'NONE' && idempotency.key_fields.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'a keyed idempotency scope declares its key fields',
        path: ['key_fields'],
      })
    }
    if (idempotency.scope === 'NONE' && idempotency.key_fields.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'key_fields is empty when there is no idempotency scope',
        path: ['key_fields'],
      })
    }
  })
export type ToolIdempotency = z.infer<typeof ToolIdempotency>

/** Side-effect annotations. `NONE` is an explicit declaration, not a missing value. */
export const ToolSideEffectAnnotation = z.enum([
  'NONE',
  'CREATES',
  'MUTATES',
  'DELETES',
  'SENDS',
  'CHARGES',
])
export type ToolSideEffectAnnotation = z.infer<typeof ToolSideEffectAnnotation>

export const ToolSideEffects = z
  .object({
    annotations: z.array(ToolSideEffectAnnotation).min(1),
    approval_required: z.boolean(),
    /**
     * MCP `destructiveHint` for a tool that ends or overwrites existing state without deleting a
     * record: a void, a correction, a changed deadline, a cancelled handoff. `DELETES` implies it;
     * this declares the rest. Additive effects (a new agreement, an invitation, an audit event)
     * never declare it.
     */
    destructive: z.literal(true).optional(),
  })
  .strict()
  .superRefine((sideEffects, ctx) => {
    if (sideEffects.destructive === true && sideEffects.annotations.includes('NONE')) {
      ctx.addIssue({
        code: 'custom',
        message: 'a read-only tool is never destructive',
        path: ['destructive'],
      })
    }
    if (sideEffects.annotations.length > 1 && sideEffects.annotations.includes('NONE')) {
      ctx.addIssue({
        code: 'custom',
        message: 'NONE is exclusive: a side-effecting tool never also declares NONE',
        path: ['annotations'],
      })
    }
    const mutating = sideEffects.annotations.some(
      (annotation) =>
        annotation === 'DELETES' || annotation === 'CHARGES' || annotation === 'SENDS',
    )
    if (mutating && !sideEffects.approval_required) {
      ctx.addIssue({
        code: 'custom',
        message: 'sending, charging or deleting requires a declared approval requirement',
        path: ['approval_required'],
      })
    }
  })
export type ToolSideEffects = z.infer<typeof ToolSideEffects>

/** Retry policy and status reconciliation after an uncertain outcome (C7, §11.6). */
export const ToolRetry = z
  .object({
    policy: z.enum(['NONE', 'BOUNDED_EXPONENTIAL', 'STATUS_RECONCILIATION']),
    max_attempts: z.number().int().positive().optional(),
    reconciliation: z.string().min(1).max(400),
  })
  .strict()
  .superRefine((retry, ctx) => {
    if (retry.policy === 'BOUNDED_EXPONENTIAL' && retry.max_attempts === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a bounded retry policy declares max_attempts',
        path: ['max_attempts'],
      })
    }
    if (retry.policy === 'NONE' && retry.max_attempts !== undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'max_attempts is only valid with a bounded retry policy',
        path: ['max_attempts'],
      })
    }
  })
export type ToolRetry = z.infer<typeof ToolRetry>

/** Names a tool may never take: there is no generic unrestricted execute tool (C7). */
export const FORBIDDEN_GENERIC_TOOL_NAMES: readonly string[] = [
  'execute',
  'execute_action',
  'execute_any_action',
  'run_any',
  'generic_execute',
  'do_anything',
]

export function isForbiddenGenericToolName(name: string): boolean {
  return FORBIDDEN_GENERIC_TOOL_NAMES.includes(name)
}

export const ToolContract = z
  .object({
    tool_name: z.string().min(1).max(128),
    tool_version: z.number().int().positive(),
    capability_id: z.string().min(1).max(128),
    required_fields: z.array(ToolFieldSpec),
    optional_fields: z.array(ToolFieldSpec),
    stable_identifiers: z.array(ToolStableIdentifier).min(1),
    pagination: ToolPagination,
    outputs: ToolOutputs,
    policy_decisions: ToolPolicyDecisions,
    idempotency: ToolIdempotency,
    side_effects: ToolSideEffects,
    retry: ToolRetry,
  })
  .strict()
  .superRefine((contract, ctx) => {
    if (isForbiddenGenericToolName(contract.tool_name)) {
      ctx.addIssue({
        code: 'custom',
        message: 'no generic unrestricted execute tool exists (C7)',
        path: ['tool_name'],
      })
    }
    const names = [...contract.required_fields, ...contract.optional_fields].map((f) => f.name)
    if (new Set(names).size !== names.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'tool field names must be unique',
        path: ['required_fields'],
      })
    }
  })
export type ToolContract = z.infer<typeof ToolContract>
